import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGetLoginOptions } from 'features/auth/hooks/use-auth';
import { useAuthState } from 'state/client-middleware';
import { KlaxAuthHero } from './shared/klax-auth-hero';
import { KlaxAuthTopBar } from './shared/klax-auth-top-bar';
import { AuthPanelLoader } from './shared/auth-panel-loader';

type HttpError = {
  message?: string;
  status?: number;
  response?: {
    status?: number;
  };
};

function isClientConfigError(error: HttpError | null | undefined): boolean {
  if (!error) return false;

  const configErrorStatuses = [403, 404, 406, 424];
  const status = error.response?.status ?? error.status;

  if (status && configErrorStatuses.includes(status)) {
    return true;
  }

  if (error.message) {
    return configErrorStatuses.some((code) => error.message?.includes(`HTTP ${code}`));
  }

  return false;
}

function isServerError(error: HttpError | null | undefined): boolean {
  if (!error) return false;

  const status = error.response?.status ?? error.status;
  if (status && status >= 500 && status < 600) {
    return true;
  }

  if (error.message) {
    const httpMatch = error.message.match(/HTTP (\d{3})/);
    if (httpMatch) {
      const statusFromMessage = parseInt(httpMatch[1], 10);
      return statusFromMessage >= 500 && statusFromMessage < 600;
    }
  }

  return false;
}

export function AuthLayout() {
  const { t } = useTranslation();
  const { isLoading, error: loginOptionsError } = useGetLoginOptions();
  const navigate = useNavigate();
  const { isAuthenticated, isMfaEnabled } = useAuthState();
  // A signed-in user is about to be sent home, so show the loader instead of the login form.
  const isRedirecting =
    isAuthenticated && !isMfaEnabled && !window.location.pathname.includes('/verify-key');

  useEffect(() => {
    if (isRedirecting) navigate('/');
  }, [isRedirecting, navigate]);

  const isAuthShellLoading = isLoading || isRedirecting;

  const renderAuthContent = () => {
    if (isAuthShellLoading) {
      return <AuthPanelLoader />;
    }

    if (isClientConfigError(loginOptionsError as HttpError | null | undefined)) {
      return (
        <div className="mx-auto w-full max-w-xl">
          <div className="relative overflow-hidden rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-red-100/50 p-8 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-red-50/80 to-transparent" />
            <div className="relative z-10">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-red-900">
                  {t('INCORRECT_PROJECT_KEY', 'Incorrect Project Key')}
                </h2>
                <div className="space-y-3 text-red-700">
                  <p className="text-base leading-relaxed">
                    {t('INCORRECT_PROJECT_KEY_DESCRIPTION', 'It seems your project is not set up in the Blocks Cloud.')}
                  </p>
                  <p className="text-sm leading-relaxed">
                    {t('CREATE_PROJECT_AT', 'Please create a project at')} {' '}
                    <a
                      href="https://cloud.seliseblocks.com"
                      className="font-semibold underline decoration-red-400 underline-offset-2 hover:decoration-red-600"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('CLOUD_URL', 'cloud.seliseblocks.com')}
                    </a>
                    , {t('THEN_UPDATE_YOUR', 'then update your')} {' '}
                    <code className="inline-flex items-center rounded-md border border-red-300/50 bg-red-200/60 px-2 py-1 font-mono text-xs text-red-800">
                      {t('ENV_FILE', '.env')}
                    </code>{' '}
                    {t('CONFIGURATION_ACCORDINGLY', 'configuration accordingly.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isServerError(loginOptionsError as HttpError | null | undefined)) {
      return (
        <div className="mx-auto w-full max-w-xl">
          <div className="relative overflow-hidden rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100/50 p-8 shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50/80 to-transparent" />
            <div className="relative z-10">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-orange-100 p-3">
                  <AlertTriangle className="h-8 w-8 text-orange-600" />
                </div>
              </div>
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold tracking-tight text-orange-900">
                  {t('SERVICES_TEMPORARILY_UNAVAILABLE', 'Services Temporarily Unavailable')}
                </h2>
                <div className="space-y-3 text-orange-700">
                  <p className="text-base leading-relaxed">
                    {t('SERVICES_TEMPORARILY_UNAVAILABLE', 'The services are temporarily unavailable.')}
                  </p>
                  <p className="text-base font-semibold leading-relaxed">
                    {t('EVERYTHING_WILL_BE_BACK_TO_NORMAL_SOON', 'Everything will be back to normal soon.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return <Outlet />;
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#e7e7e7] dark:bg-[#1a1a1a] xl:flex-row xl:p-4">
      <div className="relative hidden h-full min-h-0 w-1/2 min-w-0 overflow-hidden xl:block">
        <KlaxAuthHero className="h-full min-h-full" />
      </div>

      <div className="flex h-full w-full min-w-0 overflow-y-auto bg-white text-high-emphasis dark:bg-[#1d1d1d] xl:ml-4 xl:w-1/2">
        <div className="flex min-h-full w-full flex-col px-4 py-4 sm:px-6 sm:py-6 lg:px-10 xl:px-8 2xl:px-12">
          <div className="flex shrink-0 justify-end">
            <KlaxAuthTopBar />
          </div>
          <div className="flex flex-1 items-center justify-center py-8">
            {renderAuthContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
